import { Result, FromBody, HttpStatusCodes, ForbiddenError, NotFoundError, ConflictError, FromQuery, Inject } from '@resourceful-hono/core';
import { BaseResource } from '../BaseResource.ts';
import { GET_SCHEMA, POST_SCHEMA, DEFAULT_LIMIT, DEFAULT_ORDER } from './schema.ts';
import type { z } from 'zod';
import { Protected } from '../common/decorators.ts';
import { CashTransaction } from '../../../entities/CashTransaction/index.ts';
import { STARTING_CAPITAL_AMOUNT, MINIMUM_BALANCE, TRANSACTION_CONFLICT_TEMPLATE } from './constants.ts';
import { Permissions } from '../../../services/OAuth/enums.ts';
import { UnprocessableContentError } from '../common/errors.ts';
import { User } from '../../../entities/User.ts';
import { Account, SystemAccount } from '../../../entities/index.ts';
import { uniqueFailed } from '../../../entities/common/utils.ts';
import { Game } from '../../../entities/Game.ts';
import { DEFAULT_LAST_TRANSACTION_ID } from '../../../entities/CashTransaction/common/constants.ts';
import { GameEvent } from '../common/events.ts';
import { DataSourceTransactionScope } from '../../../common/datasource-transaction-scope.ts';
import { EventService } from '../../../services/Events.ts';

export class TransactionsResource extends BaseResource {
  @Inject()
  declare private readonly gameEvents: EventService;

  @Protected([Permissions.GetTransaction])
  public async GET(
    @FromQuery(GET_SCHEMA) query: z.infer<typeof GET_SCHEMA>
  ) {
    const game = await Game.findOne({
      where: {
        players: {
          id: this.userId,
        },
      },
    });

    if (!game)
      throw new ForbiddenError('You need to be in a game.');

    const where = query.where?.map(condition => {
      return {
        ...condition,
        game: {
          id: game.id,
        },
      };
    }) ?? { game: { id: game.id } };
    const {
      take = DEFAULT_LIMIT,
      order = DEFAULT_ORDER,
    } = query;
    const transactions = await CashTransaction.findAndCount({
      where,
      relations: {
        creditor: {
          owner: true,
        },
        debitor: {
          owner: true,
        },
      },
      order,
      take,
    });

    return Result(HttpStatusCodes.Ok, transactions);
  }

  @Protected([Permissions.PostTransaction])
  public async POST(
    @FromBody(POST_SCHEMA) body: z.infer<typeof POST_SCHEMA>
  ) {
    const user = await this.user;
    if (!user.game)
      throw new UnprocessableContentError('You need to be in a game to create transactions.');
    const from = await Account.findOne({
      where: {
        owner: { id: this.userId },
      },
    });

    if (!from)
      throw new ForbiddenError();

    const to = await Account.findOne({
      where: {
        id: body.to,
      },
    });

    if (!to)
      throw new NotFoundError(`Account with ID: ${body.to} doesn't exist.`);

    const cashTransaction = new CashTransaction();
    cashTransaction.amount = body.amount;
    cashTransaction.note = body.note;
    cashTransaction.debitor = from;
    cashTransaction.creditor = to;
    cashTransaction.game = user.game;
    cashTransaction.lastTransactionId = body.lastTransactionId;
    const balance = await from
      .getBalance(user.game.id)
      .then(balance => balance - body.amount);
    if (balance < MINIMUM_BALANCE)
      throw new UnprocessableContentError('Your balance is insufficient.');

    await using scope = await DataSourceTransactionScope.create();
    await scope
      .queryRunner
      .manager
      .save(cashTransaction)
      .catch(e => {
        if (uniqueFailed(e))
          throw new ConflictError(TRANSACTION_CONFLICT_TEMPLATE);
      });
    
    this.request.signal.throwIfAborted();
    scope.complete();
    // publish to client
    this.gameEvents.dispatchEvent(
      new GameEvent({
        gameId: cashTransaction.game.id,
        target: [this.route],
      })
    );
    const toUser = await User.findOne({
      where: {
        account: { id: to.id },
      },
      select: ['id'],
    });
    this.gameEvents.dispatchEvent(
      new GameEvent({
        gameId: cashTransaction.game.id,
        userId: toUser?.id,
        target: ['user-account'],
      })
    );
    return Result(HttpStatusCodes.Created);
  }

  public static async transferStartingCapital(user: User): Promise<void> {
    if (!user.game)
      throw new UnprocessableContentError('You need to be in a game to create transactions.');
    const bankSystemAccount = await SystemAccount.get('bank');
    await using scope = await DataSourceTransactionScope.create();
    const cashTransaction = scope
      .queryRunner
      .manager
      .create(CashTransaction, {
        amount: STARTING_CAPITAL_AMOUNT,
        debitor: bankSystemAccount,
        creditor: user.account,
        game: user.game,
      });
    cashTransaction.lastTransactionId = await user
      .account
      .getLastTransactionId(user.game.id);
    
    // user was already in the game
    // skip starting capital
    if (cashTransaction.lastTransactionId === DEFAULT_LAST_TRANSACTION_ID)
      await scope.queryRunner.manager.save(cashTransaction);
    scope.complete();
  }
}